<template>
  <div class="ethernet-settings">
    <div class="flex-grid-row mb-2">
      <toggle
        id="ethernet-toggle"
        :toggle.sync="device.network.ethernetEnabled"
      >
      </toggle>
      <div class="ml-1">
        <strong>Ethernet {{ isEthernetEnabled ? "Enabled" : "Disabled" }}</strong>
      </div>
      <div class="network-help network-help_floating">
        <a
          class="network-help__link"
          target="_blank"
          href="https://bioconnect.com/resources/install-guides/device-networking/"
        >
          Can't Connect to the Network?
        </a>
      </div>
    </div>
    <div class="form-container mt-1">
      <label class="form-label">IP Settings</label>
      <div class="form-check">
        <label
          class="radio"
          for="dhcp-enabled"
        >
          <input
            id="dhcp-enabled"
            v-model="ethernetDHCP"
            type="radio"
            :value="true"
            :disabled="!isEthernetEnabled"
          />
          <span>Automatic IP Address (DHCP)</span>
        </label>
        <label
          class="radio ml-1"
          for="dhcp-disabled"
        >
          <input
            id="dhcp-disabled"
            v-model="ethernetDHCP"
            type="radio"
            :value="false"
            :disabled="!isEthernetEnabled"
          />
          <span>Static IP Address</span>
        </label>
      </div>
    </div>
    <!-- IP Settings -->
    <div class="flex-grid">
      <template v-for="field in ipSettings">
        <div
          :key="field.id"
          class="form-container column--two"
        >
          <label class="form-label">{{ field.label }}</label>
          <input
            :id="field.id + '-input-device-settings-modal'"
            v-model="field.input"
            class="form-field"
            :class="{ 'form-field--invalid': isValid(field.id) }"
            type="text"
            :disabled="ethernetDHCP || !isEthernetEnabled"
          />
        </div>
      </template>
    </div>
    <div class="form-container mt-1">
      <label class="form-label">DNS Settings</label>
      <div class="form-check">
        <label
          class="radio"
          for="dns-enabled"
        >
          <input
            id="dns-enabled"
            v-model="ethernetStaticDNS"
            type="radio"
            :value="false"
            :disabled="!isEthernetEnabled"
          />
          <span>Automatic DNS Server</span>
        </label>
        <label
          class="radio ml-1"
          for="dns-disabled"
        >
          <input
            id="dns-disabled"
            v-model="ethernetStaticDNS"
            type="radio"
            :value="true"
            :disabled="!isEthernetEnabled"
          />
          <span>Static DNS Server</span>
        </label>
      </div>
    </div>
    <!-- DNS Settings -->
    <div class="flex-grid">
      <template v-for="field in dnsSettings">
        <div
          :key="field.id"
          class="form-container column--two"
        >
          <label class="form-label">{{ field.label }}</label>
          <input
            :id="field.id + '-input-device-settings-modal'"
            v-model="field.input"
            class="form-field"
            :class="{ 'form-field--invalid': isValid(field.id) }"
            type="text"
            :disabled="!ethernetStaticDNS || !isEthernetEnabled"
          />
        </div>
      </template>
    </div>
    <div class="modal_footer">
      <button
        id="cancel-device-settings-modal"
        class="btn btn--grey"
        @click="emitCancel"
      >
        Cancel
      </button>
      <div class="error">
        {{ errorMessage }}
      </div>
      <button
        id="submit-device-settings-modal"
        class="btn"
        type="submit"
        @click="emitSettings"
      >
        Set Ethernet
      </button>
    </div>
  </div>
</template>

<script>
import REGEX from '@/global/utilities.js'
import Toggle from '@/components/toggle.vue'

export default {
  name: 'EthernetSettings',
  components: {Toggle},
  props: ['device'],
  data() {
    return {
      errorMessage: '',
      invalidFields: [],
      ethernetDHCP: true,
      ethernetStaticDNS: false,
      ipSettings: [
        {
          id: 'ipAddressV4',
          label: 'IP Address',
          input: '',
          regex: REGEX.ipv4,
          isRequired: !this.ethernetDHCP,
        },
        {
          id: 'subnet',
          label: 'Subnet',
          input: '',
          regex: REGEX.ipv4,
          isRequired: !this.ethernetDHCP,
        },
        {
          id: 'gateway',
          label: 'Gateway',
          input: '',
          regex: REGEX.ipv4,
          isRequired: !this.ethernetDHCP,
        },
      ],
      dnsSettings: [
        {
          id: 'primaryDNS',
          label: 'Primary DNS',
          input: '',
          regex: REGEX.ipv4,
          isRequired: this.ethernetStaticDNS,
        },
        {
          id: 'secondaryDNS',
          label: 'Secondary DNS',
          input: '',
          regex: REGEX.ipv4,
          isRequired: this.ethernetStaticDNS,
        },
      ],
    }
  },
  computed: {
    isEthernetEnabled() {
      return this.device.network.ethernetEnabled
    },
  },
  watch: {
    ethernetDHCP: {
      immediate: true,
      handler() {
        this.ipSettings.forEach((setting) => {
          setting.isRequired = !this.ethernetDHCP
        })
      },
    },
    ethernetStaticDNS: {
      immediate: true,
      handler() {
        this.dnsSettings.forEach((setting) => {
          setting.isRequired = this.ethernetStaticDNS
        })
      },
    },
  },
  created() {
    if (this.device.network) {
      this.assignNetworkSettings()
    }
  },
  methods: {
    emitCancel() {
      this.$emit('cancel')
    },
    emitSettings() {
      this.errorMessage = ''
      this.checkInvalidFields()
      if (this.invalidFields.length > 0) {
        this.errorMessage = 'Please check if the settings you have entered are valid.'
      } else {
        const _device = this.combineSettings()
        this.$emit('submit-ethernet', _device)
      }
    },
    isValid(fieldID) {
      const _isInvalid = this.invalidFields.filter((invalidField) => {
        return invalidField.id === fieldID
      })
      return _isInvalid.length > 0
    },
    combineSettings() {
      const _settingsInput = {}
      const _networkFields = [...this.ipSettings, ...this.dnsSettings]
      _networkFields.forEach((field) => {
        _settingsInput[field.id] = field.input
      })
      const _newDeviceSettings = {
        ...this.device,
        network: {
          ...this.device.network,
          ..._settingsInput,
          dhcp: this.ethernetDHCP,
          staticDNS: this.ethernetStaticDNS,
        },
      }
      return _newDeviceSettings
    },
    assignNetworkSettings() {
      const {network} = this.device
      this.ethernetDHCP = network.dhcp
      this.ethernetStaticDNS = network.staticDNS

      if (!this.ethernetDHCP) {
        this.ipSettings = this.ipSettings.map((setting) => {
          setting.input = network[setting.id]
          return setting
        })
      }

      if (this.ethernetStaticDNS) {
        this.dnsSettings = this.dnsSettings.map((setting) => {
          setting.input = network[setting.id]
          return setting
        })
      }
    },
    checkInvalidFields() {
      this.invalidFields = []
      const _fields = [...this.ipSettings, ...this.dnsSettings]
      const _requiredMissingInput = _fields.filter((field) => {
        return field.isRequired && !field.input
      })
      const _patternMismatch = _fields.filter((field) => {
        if (field.input && field.regex) {
          return !field.regex.test(field.input)
        }
      })
      this.invalidFields = [..._requiredMissingInput, ..._patternMismatch]
    },
  },
}
</script>

<style lang="scss" scoped>
.network-help {
  font-size: $small-font;
  &_floating {
    @include flex-display();
    @include flex-justify-content(flex-end);
    flex: 1;
  }
  &__link {
    text-decoration: underline;
    color: $dark-grey;
  }
}
</style>
